import { HttpClient } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { isEmpty } from "lodash";
import { Subscription } from "rxjs";
import { normalizeCandidateSessionConfigurations } from "src/app/cbta-web/ratings/container-ratings-form/shared-v2/utils/session-form.utils";
import { RouteType } from "src/app/models/enum";
import { Candidate, SessionDetails, TrainingDetails } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";

@Component({
	selector: "app-training-details",
	templateUrl: "./training-details.component.html",
	styleUrls: ["./training-details.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TrainingDetailsComponent implements OnInit, OnDestroy {
	id?: string;

	sessionSub!: Subscription;
	routeSub!: Subscription;

	selected: Candidate[][] = [];

	private _trainingDetails?: TrainingDetails;

	constructor(
		private route: ActivatedRoute,
		private loaderService: LoaderService,
		private roleService: RoleService,
		private sharedService: SharedService,
		private httpService: HttpService,
		private http: HttpClient,
	) {}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.REPORT);
		this.initSessions();
	}

	get isNotPilot(): boolean {
		return (
			this.roleService.hasInstructorView ||
			this.roleService.isAdmin ||
			this.roleService.isOfficeAdmin ||
			this.roleService.isGroupAdmin
		);
	}

	sessionTitle(session: SessionDetails): string {
		return normalizeCandidateSessionConfigurations(session.sessionConfigurations)
			.map((config) => config.description?.trim() ?? "")
			.filter(Boolean)
			.join(", ");
	}

	get routeType() {
		return RouteType;
	}

	get hasNoSessions(): boolean {
		return isEmpty(this.trainingDetails?.sessions);
	}

	setSelected(index: number, selected: Candidate[]) {
		this.selected[index] = selected;
	}

	setTrainingDetails(trainingDetails?: TrainingDetails): void {
		this._trainingDetails = trainingDetails;

		setTimeout(() => {
			for (const [i, session] of (trainingDetails?.sessions ?? []).entries()) {
				this.selected[i] = session?.candidates ?? [];
			}
		});
	}

	get trainingDetails(): TrainingDetails | undefined {
		return this._trainingDetails;
	}

	initSessions(): void {
		this.loaderService.setLoading(true);

		this.routeSub = this.route.params.subscribe((params) => {
			this.id = params["id"];

			if (!isEmpty(this.id)) {
				this.fetchSessions();
			}
		});
	}

	handleSearch(candidateName: HTMLInputElement, staffNumber: HTMLInputElement): void {
		const candidateValue = candidateName.value;
		const staffNumberValue = staffNumber.value;

		if (!isEmpty(candidateValue) || !isEmpty(staffNumberValue)) {
			const filters = {
				...(candidateValue && { candidateName: candidateValue }),
				...(staffNumberValue && { staffNumber: staffNumberValue }),
			};

			this.fetchSessions(filters);
		}
	}

	clearSearch(candidateName: HTMLInputElement, staffNumber: HTMLInputElement): void {
		candidateName.value = "";
		staffNumber.value = "";

		this.fetchSessions(); // fetch all without filters
	}

	private fetchSessions(filters: any = {}): void {
		this.loaderService.setLoading(true);

		this.sessionSub = this.httpService
			.getSessions({
				formId: this.id,
				role: this.roleService?.getUserRole(),
				...filters,
			})
			.subscribe((result) => {
				const sessionDetails = result.data as TrainingDetails;
				const sessions = (sessionDetails.sessions ?? []).map((session) => ({
					...session,
					candidates: this.sortCandidatesByRecentActivity(session.candidates ?? []),
				}));

				this.setTrainingDetails({
					...sessionDetails,
					sessions,
				});

				this.loaderService.setLoading(false);
			});
	}

	getFilteredCandidates(candidates: Candidate[]): Candidate[] {
		if (this.roleService.hasPilotView) {
			return candidates.filter((candidate) => candidate.candidateId === this.roleService.userDetails.id);
		} else {
			return candidates;
		}
	}

	ngOnDestroy(): void {
		if (this.sessionSub) {
			this.sessionSub.unsubscribe();
		}
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}
	}

	handleChange(event: Event): void {
		const target = event.target as HTMLInputElement;

		if (target) {
			const value = target.value;

			// TODO: filter auto suggest options if want
		}
	}

	async download(candidate: Candidate) {
		this.loaderService.setLoading(true);

		const url = candidate.summaryFileSignedUrl;

		if (url) {
			this.sharedService.download(url);
			this.loaderService.setLoading(false);
			return;
		}

		// if pdf is null generate new one before downloading it
		this.httpService
			.generateSummary(candidate.candidateId, {
				downloadOnly: true,
				role: this.roleService.getUserRole(),
			})
			.subscribe(
				(result) => {
					const data = result.data;

					const filename = data.report_filename;

					this.sharedService.download(filename);

					this.loaderService.setLoading(false);
				},
				(error) => {
					console.error(error);
					this.loaderService.setLoading(false);
				},
			);
	}

	async downloadAll(index: number) {
		this.loaderService.setLoading(true);

		const ids = this.selected[index].filter((candidate) => !this.isPendingCandidateSignOff(candidate));

		for (const id of ids) {
			const url = id.summaryFileSignedUrl;

			if (url) {
				this.sharedService.download(url);
				this.loaderService.setLoading(false);
			} else {
				// if pdf is null generate new one before downloading it
				this.httpService
					.generateSummary(id.candidateId, {
						downloadOnly: true,
						role: this.roleService.getUserRole(),
					})
					.subscribe(
						(result) => {
							const data = result.data;

							const filename = data.report_filename;

							this.sharedService.download(filename);

							this.loaderService.setLoading(false);
						},
						(error) => {
							console.error(error);
							this.loaderService.setLoading(false);
						},
					);
			}
			await new Promise<void>((resolve) => setTimeout(resolve, 1000));
		}
	}

	get rs() {
		return this.roleService;
	}

	private isPendingCandidateSignOff(candidate: Candidate): boolean {
		if (candidate.isPendingCandidateSignOff != null) {
			return candidate.isPendingCandidateSignOff;
		}
		return candidate.userId === candidate.candidateId && !candidate.summaryFilename;
	}

	private sortCandidatesByRecentActivity(candidates: Candidate[]): Candidate[] {
		const toTime = (d?: string | null): number => (d ? new Date(d).getTime() : Number.NEGATIVE_INFINITY);

		const activityTime = (c: Candidate): number => {
			const fromActivity = toTime(c.activityAt);
			if (fromActivity !== Number.NEGATIVE_INFINITY) {
				return fromActivity;
			}
			return Math.max(toTime(c.signOffDate), toTime(c.completeDate));
		};

		return [...candidates].sort((a, b) => activityTime(b) - activityTime(a));
	}
}
